import { useEffect, useState } from "react";
import {
    Breadcrumb,
    Layout,
    Card,
    Descriptions,
    Tag,
    Button,
    Spin,
    message,
    Row,
    Typography,
    Table
} from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Content } from "antd/es/layout/layout";
import Headerdiv from "../../layout/header";
import { APPBACKGROUND_COLOR } from "../../appsettings";
import dayjs from "dayjs";
import Footerdiv from "../../layout/footer";
import axiosClient from "../../../api/axiosClient";

const { Title } = Typography;

export const TrainerView = () => {
    const [trainer, setTrainer] = useState(null);
    const [loading, setLoading] = useState(true);

    const [metrics, setMetrics] = useState([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [assignedMembers, setAssignedMembers] = useState([]);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTrainerDetails = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await axiosClient.get(`/trainer/get_trainer_by_id.php?id=${id}`);
                const data = response.data;

                if (data.status === "success") {
                    setTrainer(data.data);
                } else {
                    message.error(data.message || "Trainer not found");
                    navigate("/trainers/view");
                }
            } catch (error) {
                console.error("Fetch error:", error);
                message.error(error.response?.data?.message || "Network error or server issue");
            } finally {
                setLoading(false);
            }
        };

        fetchTrainerDetails();
    }, [id, navigate]);

    // Fetch Metrics
    const fetchMetrics = async (page = 1, pageSize = 10) => {
        if (!id) return;
        setMetricsLoading(true);
        try {
            const response = await axiosClient.get(
                `/trainer/metric/get_all_trainer_metrics_by_id.php?trainer_id=${id}&page=${page}&pageSize=${pageSize}`
            );
            const data = response.data;

            if (data.status === "success") {
                setMetrics(data.data);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: data.total_records,
                });
            } else {
                console.warn(data.message);
            }
        } catch (error) {
            console.error("Metrics fetch error:", error);
            message.error(error.response?.data?.message || "Failed to fetch metrics.");
        } finally {
            setMetricsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchMetrics(pagination.current, pagination.pageSize);
        }
    }, [id]);

    // Fetch Assigned Members
    useEffect(() => {
        const fetchAssignedMembers = async () => {
            if (!id) return;
            try {
                const response = await axiosClient.get(`/trainer/get_assigned_members.php?trainer_id=${id}`);
                if (response.data.status === "success") {
                    setAssignedMembers(response.data.data || []);
                }
            } catch (error) {
                console.error("Error fetching assigned members:", error);
            }
        };

        fetchAssignedMembers();
    }, [id]);

    const handleTableChange = (newPagination) => {
        fetchMetrics(newPagination.current, newPagination.pageSize);
    };

    const createMarkup = (html) => {
        return { __html: html };
    };

    const metricColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => text ? dayjs(text).format("DD MMM YYYY hh:mm A") : "N/A",
        },
        {
            title: 'Weight (kg)',
            dataIndex: 'weight',
            key: 'weight',
        },
        {
            title: 'Height (cm)',
            dataIndex: 'height',
            key: 'height',
        },
        {
            title: 'BMI',
            dataIndex: 'bmi',
            key: 'bmi',
        },
        {
            title: 'Body Fat (%)',
            dataIndex: 'body_fat',
            key: 'body_fat',
        },
        {
            title: 'Muscle Mass (kg)',
            dataIndex: 'muscle_mass',
            key: 'muscle_mass',
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading Trainer Details...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item>
                            <Link to="/trainers/view">Trainers</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Trainer Details</Breadcrumb.Item>
                    </Breadcrumb>

                    {trainer && (
                        <Card
                            title={<Title level={4} style={{ margin: 0 }}>{trainer.name}</Title>}
                            extra={
                                <Link to={`/trainers/edit/${id}`}>
                                    <Button type="primary" icon={<EditOutlined />}>
                                        Edit Trainer
                                    </Button>
                                </Link>
                            }
                        >
                            <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                                <Descriptions.Item label="Trainer ID">{trainer.trainer_id}</Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={trainer.status === 1 || trainer.status === true ? "green" : "red"}>
                                        {trainer.status === 1 || trainer.status === true ? "ACTIVE" : "INACTIVE"}
                                    </Tag>
                                </Descriptions.Item>

                                <Descriptions.Item label="NIC">{trainer.nic}</Descriptions.Item>
                                <Descriptions.Item label="Gender">
                                    {trainer.gender === 'M' ? 'Male' : trainer.gender === 'F' ? 'Female' : 'Other'}
                                </Descriptions.Item>

                                <Descriptions.Item label="Birthday">
                                    {trainer.birthday ? dayjs(trainer.birthday).format("DD MMM YYYY") : "N/A"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Age">
                                    {trainer.birthday ? dayjs().diff(dayjs(trainer.birthday), 'year') : "N/A"} Years
                                </Descriptions.Item>

                                <Descriptions.Item label="Contact Number">{trainer.contact_no}</Descriptions.Item>
                                <Descriptions.Item label="Emergency Contact">{trainer.emergency_contact}</Descriptions.Item>

                                <Descriptions.Item label="Address" span={2}>{trainer.address}</Descriptions.Item>

                                {/* Trainer Specific Fields */}
                                <Descriptions.Item label="Specialization">
                                    <Tag color="blue">{trainer.specialization || "General"}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Experience">
                                    {trainer.experience_years} Years
                                </Descriptions.Item>
                            </Descriptions>

                            {/* Health/Notes Section (Rich Text) */}
                            <div style={{ marginTop: "20px" }}>
                                <Title level={5}>Health & Medical Notes</Title>
                                <Card type="inner" style={{ backgroundColor: "#f9f9f9" }}>
                                    {trainer.health_conditions ? (
                                        <div dangerouslySetInnerHTML={createMarkup(trainer.health_conditions)} />
                                    ) : (
                                        <span style={{ color: "#999" }}>No notes recorded.</span>
                                    )}
                                </Card>
                            </div>

                            {/* Metrics Table Section */}
                            <div style={{ marginTop: "30px" }}>
                                <Title level={5}>Performance Metrics</Title>
                                <Table
                                    columns={metricColumns}
                                    dataSource={metrics}
                                    rowKey="id"
                                    pagination={pagination}
                                    loading={metricsLoading}
                                    onChange={handleTableChange}
                                    bordered
                                    size="small"
                                />
                            </div>

                            {/* Assigned Members */}
                            <div style={{ marginTop: "20px" }}>
                                <Title level={5}>Assigned Members</Title>
                                <Table
                                    dataSource={assignedMembers}
                                    rowKey="id"
                                    size="small"
                                    bordered
                                    pagination={false}
                                    locale={{ emptyText: "No members assigned" }}
                                    columns={[
                                        {
                                            title: 'Member ID',
                                            dataIndex: 'member_id',
                                            key: 'member_id',
                                        },
                                        {
                                            title: 'Name',
                                            dataIndex: 'name',
                                            key: 'name',
                                        },
                                        {
                                            title: 'NIC',
                                            dataIndex: 'nic',
                                            key: 'nic',
                                        },
                                        {
                                            title: 'Contact',
                                            dataIndex: 'contact_no',
                                            key: 'contact_no',
                                        },
                                    ]}
                                />
                            </div>

                            <Row justify="end" style={{ marginTop: 20 }}>
                                <Button onClick={() => navigate("/trainers/view")} icon={<ArrowLeftOutlined />}>
                                    Back to List
                                </Button>
                            </Row>
                        </Card>
                    )}
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};